/* ============================================================
   ÉTAPE 7: MISES EN PLACE (Refactorisée)
   ============================================================ */

import { useImageUpload } from '../../../../hooks/useImageUpload';
import { LayoutTemplates } from '../LayoutTemplates';
import { LayoutList } from '../LayoutList';
import { AutoOtherInfo } from '../AutoOtherInfo';

interface RoomLayout {
  id: string;
  name: string;
  description: string;
  imageUrl: string;
  minCapacity: number | null;
  maxCapacity: number | null;
  order: number;
  isUploading?: boolean;
  pendingFile?: File;
  previewUrl?: string;
}

interface Step7RoomLayoutsProps {
  data: any;
  onChange: (updates: any) => void;
  onSave?: () => Promise<void>;
}

export function Step7RoomLayouts({ data, onChange, onSave }: Step7RoomLayoutsProps) {
  const moduleEnabled = !!data.roomLayoutsEnabled;
  const layouts: RoomLayout[] = data.roomLayouts || [];
  const { createPreview } = useImageUpload();

  const setModuleEnabled = (enabled: boolean) => {
    onChange({ roomLayoutsEnabled: enabled, roomLayouts: enabled ? layouts : [] });
  };

  const updateLayouts = (newLayouts: RoomLayout[]) => {
    onChange({ roomLayouts: newLayouts });
  };

  const addLayout = (template?: any) => {
    const newLayout: RoomLayout = {
      id: `layout_${Date.now()}`,
      name: template?.name || '',
      description: template?.description || '',
      imageUrl: '',
      minCapacity: template?.minCapacity || null,
      maxCapacity: template?.maxCapacity || null,
      order: layouts.length,
      isUploading: false,
    };
    updateLayouts([...layouts, newLayout]);
  };

  const updateLayout = (id: string, updates: Partial<RoomLayout>) => {
    updateLayouts(layouts.map((l) => (l.id === id ? { ...l, ...updates } : l)));
  };

  const deleteLayout = (id: string) => {
    const layout = layouts.find((l) => l.id === id);
    if (layout?.previewUrl?.startsWith('blob:')) URL.revokeObjectURL(layout.previewUrl);
    updateLayouts(layouts.filter((l) => l.id !== id));
    setTimeout(() => onSave?.(), 100);
  };

  const handleImageUpload = (id: string, event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) return alert('Fichier trop volumineux (max 5 MB)');
    
    const previewUrl = createPreview(file);
    updateLayout(id, { previewUrl, pendingFile: file });
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '2rem' }}>
      <div>
        <label
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: '1rem',
            padding: '1.25rem',
            background: moduleEnabled ? 'rgba(0,117,141,0.05)' : 'white',
            border: `2px solid ${moduleEnabled ? '#00758d' : '#e5e7eb'}`,
            borderRadius: '12px',
            cursor: 'pointer',
            transition: 'all 0.2s ease',
          }}
        >
          <input
            type="checkbox"
            checked={moduleEnabled}
            onChange={(e) => setModuleEnabled(e.target.checked)}
            style={{ width: '20px', height: '20px', marginTop: '2px', cursor: 'pointer', accentColor: '#00758d' }}
          />
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: '1rem', fontWeight: 600, color: '#00303c', marginBottom: '0.25rem' }}>Activer les mises en place</div>
            <div style={{ fontSize: '0.9rem', color: '#666' }}>Permet aux clients de choisir la configuration de salle souhaitée...</div>
          </div>
        </label>
      </div>

      {moduleEnabled && (
        <div style={{ animation: 'fadeIn 0.3s ease' }}>
          <LayoutTemplates onAdd={addLayout} />
          
          <LayoutList 
            layouts={layouts} 
            onAdd={() => addLayout()} 
            onUpdate={updateLayout} 
            onDelete={deleteLayout} 
            onImageUpload={handleImageUpload} 
          />

          <AutoOtherInfo />
        </div>
      )}
    </div>
  );
}
